import React from "react";

/**
 * Render-time crash isolator. Wraps any subtree (typically a page) so a
 * thrown error inside the tree shows a friendly fallback rather than
 * propagating to the root and producing a blank white page.
 *
 * The fix here addresses the v1.2.0 bug where Admin.jsx could blank out
 * if any one of its 10+ data fetches returned an unexpected payload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console so it's debuggable in DevTools
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const msg = (this.state.error && this.state.error.message) || String(this.state.error || "Unknown error");
      return (
        <div data-testid="error-boundary-fallback" className="m-8 p-6 bg-card border border-[hsl(var(--critical))]/30 rounded-sm max-w-2xl">
          <div className="label-mono text-[hsl(var(--critical))] mb-2">Render error</div>
          <div className="font-heading text-lg mb-2 tracking-tight">Something went wrong on this page.</div>
          <div className="text-sm text-muted-foreground mb-4 break-words">{msg}</div>
          <div className="flex gap-2">
            <button onClick={this.reset} data-testid="error-boundary-retry" className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90">
              Try again
            </button>
            <button onClick={() => window.location.reload()} className="h-9 px-4 border border-border text-sm rounded-sm">
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
